/**
 * Data Table Tool Generator — finds data-table elements or table-like patterns
 * in the UI tree and generates search, filter, and paginate tools.
 */

import type { CogoElement, CogoBlockDef, DataTableColumn } from "@cogo/types"
import type { WebMCPTool, JsonSchema } from "../core/tool-registry"
import type { GeneratorRuntime } from "./form-tools"

// ── Types ──────────────────────────────────────────────────

interface TableInfo {
  tableId: string
  label: string
  columns: DataTableColumn[]
  hasSearch: boolean
  searchInputId?: string
  hasPagination: boolean
  dataSource?: string  // #{ui.items} etc.
}

// ── Detection ──────────────────────────────────────────────

/**
 * Detect data-table elements or table-like patterns (iterateOver + grid header).
 */
function findTables(root: CogoElement): TableInfo[] {
  const tables: TableInfo[] = []

  function walk(el: CogoElement): void {
    // Explicit data-table type with columns property
    if (el.type === "data-table" && el.columns) {
      tables.push({
        tableId: el.id,
        label: el.id,
        columns: el.columns,
        hasSearch: false,
        hasPagination: el.serverPaginated === true || el.serverPaginated === "true",
        dataSource: el.iterateOver ?? el.bind,
      })
      return
    }

    // Detect table-like pattern: container with iterateOver (the row template)
    if (el.iterateOver && el.children && el.children.length > 0) {
      // This is likely a row template — look at parent context for search/pagination
      const columns = inferColumnsFromRowTemplate(el)
      if (columns.length > 0) {
        tables.push({
          tableId: el.id,
          label: el.id,
          columns,
          hasSearch: false,
          hasPagination: false,
          dataSource: el.iterateOver,
        })
      }
    }

    // Recurse
    for (const child of el.children ?? []) {
      walk(child)
    }
  }

  walk(root)

  // Post-process: detect search inputs and pagination near tables
  detectSearchAndPagination(root, tables)

  return tables
}

/**
 * Infer columns from a repeating row template by examining its children.
 */
function inferColumnsFromRowTemplate(rowTemplate: CogoElement): DataTableColumn[] {
  const columns: DataTableColumn[] = []
  const itemVar = rowTemplate.itemVar ?? "item"

  function extractColumnFromChild(child: CogoElement): void {
    // Look for text elements referencing #{item.xxx}
    if (child.type === "text" && child.text) {
      const match = /^#\{(\w+)\.(\w+)\}$/.exec(child.text.replace(/^\$/, ""))
      if (match && match[1] === itemVar) {
        columns.push({
          key: match[2],
          header: child.id.replace(/^(cell-|col-|td-)/, "").replace(/-/g, " "),
          type: "text",
        })
        return
      }
      // More complex expressions like "#{item.sellingPrice}" embedded in text
      const re = new RegExp(`#\\{${itemVar}\\.(\\w+)\\}`)
      const m = re.exec(child.text)
      if (m) {
        columns.push({
          key: m[1],
          header: child.id.replace(/^(cell-|col-|td-)/, "").replace(/-/g, " "),
          type: "text",
        })
        return
      }
    }
    if (child.type === "badge" && child.text) {
      const re = new RegExp(`#\\{${itemVar}\\.(\\w+)\\}`)
      const m = re.exec(child.text)
      if (m) {
        columns.push({
          key: m[1],
          header: child.id.replace(/^(cell-|col-|td-)/, "").replace(/-/g, " "),
          type: "badge",
        })
        return
      }
    }
    // Recurse into nested containers
    for (const grandchild of child.children ?? []) {
      extractColumnFromChild(grandchild)
    }
  }

  for (const child of rowTemplate.children ?? []) {
    extractColumnFromChild(child)
  }

  return columns
}

/**
 * Walk tree to find search inputs and pagination buttons near table elements.
 */
function detectSearchAndPagination(root: CogoElement, tables: TableInfo[]): void {
  if (tables.length === 0) return

  // Find all search inputs and pagination elements
  function walk(el: CogoElement): void {
    // Search: input with placeholder containing "search"
    if (el.type === "input" && el.placeholder && /search/i.test(el.placeholder)) {
      // Assign to nearest table (first one)
      if (tables.length > 0) {
        tables[0].hasSearch = true
        tables[0].searchInputId = el.id
      }
    }

    // Pagination: button with text "Previous" or "Next" or events containing "page"
    if (el.type === "button" && el.events?.tap) {
      const flowName = typeof el.events.tap === "string" ? el.events.tap : el.events.tap.flow
      if (flowName && /page|paginate/i.test(flowName ?? "")) {
        if (tables.length > 0) {
          tables[0].hasPagination = true
        }
      }
    }
    if (el.type === "button" && el.text && /previous|next|prev/i.test(el.text) && !el.text.startsWith("#{")) {
      if (tables.length > 0) {
        tables[0].hasPagination = true
      }
    }

    for (const child of el.children ?? []) {
      walk(child)
    }
  }

  walk(root)
}

function sanitizeToolName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase()
}

// ── Tool generation ────────────────────────────────────────

export function generateDataTableTools(
  block: CogoBlockDef,
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  if (!block.root) return []
  const tables = findTables(block.root)
  const tools: WebMCPTool[] = []

  for (const table of tables) {
    const tableNameClean = sanitizeToolName(table.tableId)

    // search_{tableId} tool
    if (table.hasSearch) {
      tools.push({
        name: `search_${tableNameClean}`,
        description: narratives?.[`search_${tableNameClean}`]?.effect
          ?? `Search table "${table.label}" by keyword`,
        inputSchema: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Search query string",
            },
          },
          required: ["query"],
        },
        handler: createSearchHandler(table),
        metadata: {
          blockName: block.blockType,
          readOnly: false,
          tags: ["data-table", "search"],
        },
      })
    }

    // filter_{tableId} tool — one param per column
    if (table.columns.length > 0) {
      const filterProps: Record<string, JsonSchema> = {}
      for (const col of table.columns) {
        filterProps[col.key] = {
          type: "string",
          description: `Filter by ${col.header ?? col.key}`,
        }
      }

      tools.push({
        name: `filter_${tableNameClean}`,
        description: `Filter table "${table.label}" by column values. Available columns: ${table.columns.map((c) => c.key).join(", ")}`,
        inputSchema: {
          type: "object",
          properties: filterProps,
        },
        handler: createFilterHandler(table),
        metadata: {
          blockName: block.blockType,
          readOnly: false,
          tags: ["data-table", "filter"],
        },
      })
    }

    // paginate_{tableId} tool
    if (table.hasPagination) {
      tools.push({
        name: `paginate_${tableNameClean}`,
        description: `Navigate pages of table "${table.label}"`,
        inputSchema: {
          type: "object",
          properties: {
            page: {
              type: "number",
              description: "Page number (0-indexed)",
            },
            direction: {
              type: "string",
              enum: ["next", "prev", "first", "last"],
              description: "Page direction (alternative to page number)",
            },
          },
        },
        handler: createPaginateHandler(table),
        metadata: {
          blockName: block.blockType,
          readOnly: false,
          tags: ["data-table", "paginate"],
        },
      })
    }

    // sort_{tableId} tool
    tools.push({
      name: `sort_${tableNameClean}`,
      description: `Sort table "${table.label}" by a column`,
      inputSchema: {
        type: "object",
        properties: {
          tableId: { type: "string", description: "Table identifier" },
          column: { type: "string", description: "Column key to sort by" },
          direction: { type: "string", enum: ["asc", "desc"], description: "Sort direction" },
        },
        required: ["tableId", "column", "direction"],
      },
      handler: createSortHandler(table),
      metadata: {
        blockName: block.blockType,
        readOnly: false,
        tags: ["data-table", "sort"],
      },
    })

    // export_data tool
    tools.push({
      name: `export_${tableNameClean}`,
      description: `Export data from table "${table.label}" as CSV or JSON`,
      inputSchema: {
        type: "object",
        properties: {
          tableId: { type: "string", description: "Table identifier" },
          format: { type: "string", enum: ["csv", "json"], description: "Export format" },
        },
        required: ["tableId"],
      },
      handler: createExportHandler(table),
      metadata: {
        blockName: block.blockType,
        readOnly: true,
        tags: ["data-table", "export"],
      },
    })

    // select_row tool
    tools.push({
      name: `select_row_${tableNameClean}`,
      description: `Select a row in table "${table.label}" by index`,
      inputSchema: {
        type: "object",
        properties: {
          tableId: { type: "string", description: "Table identifier" },
          rowIndex: { type: "number", description: "Row index to select" },
        },
        required: ["tableId", "rowIndex"],
      },
      handler: createSelectRowHandler(table),
      metadata: {
        blockName: block.blockType,
        readOnly: false,
        tags: ["data-table", "select"],
      },
    })
  }

  return tools
}

function createSearchHandler(
  table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      const query = params.query as string
      runtime.stateManager.setState("ui.searchQuery", query)
      // Trigger search flow if available
      return runtime.actionEngine.executeFlow("searchItems", { value: query })
    }
  }
}

function createFilterHandler(
  table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      // Set filter state for each provided column
      for (const [key, value] of Object.entries(params)) {
        runtime.stateManager.setState(`ui.filters.${key}`, value)
      }
      return { success: true, filters: params }
    }
  }
}

function createPaginateHandler(
  _table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      if (params.page !== undefined) {
        return runtime.actionEngine.executeFlow("changePage", { page: params.page })
      }
      if (params.direction) {
        const currentPage = (runtime.stateManager.get("ui.currentPage") as number) ?? 0
        const totalPages = (runtime.stateManager.get("ui.totalPages") as number) ?? 1
        let targetPage = currentPage
        switch (params.direction) {
          case "next": targetPage = Math.min(currentPage + 1, totalPages - 1); break
          case "prev": targetPage = Math.max(currentPage - 1, 0); break
          case "first": targetPage = 0; break
          case "last": targetPage = totalPages - 1; break
        }
        return runtime.actionEngine.executeFlow("changePage", { page: targetPage })
      }
      return { success: false, error: "Provide page number or direction" }
    }
  }
}

function createSortHandler(
  table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      const ds = table.dataSource?.replace(/^#\{/, "").replace(/\}$/, "") ?? "ui.tableData"
      runtime.stateManager.setState(`${ds}.__sort`, {
        column: params.column,
        direction: params.direction,
      })
      return { success: true, column: params.column, direction: params.direction }
    }
  }
}

function createExportHandler(
  table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      const ds = table.dataSource?.replace(/^#\{/, "").replace(/\}$/, "") ?? "ui.tableData"
      const data = runtime.stateManager.get(ds) as unknown[] | undefined
      const format = (params.format as string) ?? "json"

      if (!data || !Array.isArray(data)) {
        return { success: false, error: "No data found", format }
      }

      if (format === "csv") {
        if (data.length === 0) return { success: true, format, data: "" }
        const headers = Object.keys(data[0] as Record<string, unknown>)
        const rows = data.map(row => headers.map(h => String((row as Record<string, unknown>)[h] ?? "")).join(","))
        return { success: true, format, data: [headers.join(","), ...rows].join("\n") }
      }

      return { success: true, format, data: JSON.stringify(data) }
    }
  }
}

function createSelectRowHandler(
  table: TableInfo,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      const ds = table.dataSource?.replace(/^#\{/, "").replace(/\}$/, "") ?? "ui.tableData"
      runtime.stateManager.setState(`${ds}.__selectedRow`, params.rowIndex)
      return { success: true, rowIndex: params.rowIndex }
    }
  }
}
